/*
  EVO Kalorienrechner (sections/evo-calc-calories.liquid).
  Vanilla port of the former Vue calculator (assets/calorie-calculator.js). calculate() keeps its formulas,
  constants, order of steps and rounding 1:1 — do not "fix" them here without changing the guide copy too.

  Old → new behaviour:
  - Required: Geschlecht, Alter, Gewicht, Größe, KFA, Aktivität, Ziel (the old widget also required the KFA).
  - Results update live once every field is valid (old: "Jetzt berechnen" button). The old inputs declared
    min/max (Alter 1–99, Gewicht 30–500, Größe 50–250) but never enforced them; here they gate the live result
    so half-typed values ("8" on the way to "80 kg") don't flash nonsense. KFA had no limits; 3–70 % here.
  - Goal change sets the default rate (lose 500, gain 300, maintain 0) like setInitialRate() did.
*/
(() => {
  const RATE_DEFAULT = { lose: 500, gain: 300, maintain: 0 };
  const LIMITS = { age: [1, 99], weight: [30, 500], height: [50, 250], kfa: [3, 70] };
  const NAMES = { age: 'Alter', weight: 'Gewicht', height: 'Größe', kfa: 'Körperfettanteil' };
  const UNITS = { age: 'Jahre', weight: 'kg', height: 'cm', kfa: '%' };
  // short messages: Alter/Gewicht/Größe sit in narrow columns on phones
  const EMPTY_MSG = {
    age: 'Bitte Alter eingeben.',
    weight: 'Bitte Gewicht eingeben.',
    height: 'Bitte Größe eingeben.',
    kfa: 'Bitte Körperfettanteil eingeben – die Beispiele helfen beim Schätzen.',
  };

  // ---- the calculation (1:1 from the Vue bundle's calculateMacros) ----
  function calculate(f) {
    const { gender, age, weight, height, kfa, activity, goal, rate } = f;

    // Harris-Benedict (revised, Roza & Shipman 1984)
    const bmr =
      gender === 'male'
        ? 88.362 + 13.397 * weight + 4.799 * height - 5.677 * age
        : 447.593 + 9.247 * weight + 3.098 * height - 4.33 * age;
    const tdee = bmr * activity;
    const adjust = goal === 'lose' ? -rate : goal === 'gain' ? rate : 0;
    let calories = tdee + adjust;

    // protein: 2 g per kg of (adjusted) lean mass; above 10 % body fat the excess fat share is taken off
    let leanMass = weight;
    if (kfa > 10) leanMass = weight * (1 - (kfa / 100 - 0.1));
    const protein = 2 * leanMass;
    const proteinKcal = protein * 4;

    const FAT_MIN = 0.7; // g per kg
    const FAT_STD = 0.8; // g per kg
    const CARB_MIN = 1; // g per kg

    let aggressive = false;
    let carbsZeroed = false;
    let recommended = null;

    let fat = weight * FAT_STD;
    let fatKcal = fat * 9;
    let carbsKcal = calories - proteinKcal - fatKcal;
    let carbs = carbsKcal / 4;

    if (carbs < weight * CARB_MIN) {
      fat = weight * FAT_MIN;
      fatKcal = fat * 9;
      carbsKcal = calories - proteinKcal - fatKcal;
      carbs = carbsKcal / 4;
      if (carbs < weight * 0.5) {
        aggressive = true;
        if (carbsKcal < 0) {
          carbs = 0;
          carbsKcal = 0;
          calories = proteinKcal + fatKcal;
          carbsZeroed = true;
        }
      }
    }

    // kept from the original (never true in practice, fat is at most weight × 0.8 at this point)
    if (goal === 'gain' && fat > weight * FAT_STD) {
      fat = weight * FAT_STD;
      fatKcal = fat * 9;
      carbsKcal = calories - proteinKcal - fatKcal;
      carbs = carbsKcal / 4;
    }

    if (goal === 'lose' && rate > weight * 10) {
      recommended = Math.round(weight * 7);
    }

    return {
      calories: Math.round(calories),
      protein: parseFloat(protein.toFixed(1)),
      carbs: parseFloat(Math.max(0, carbs).toFixed(1)),
      fat: parseFloat(fat.toFixed(1)),
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      aggressive,
      carbsZeroed,
      recommended,
    };
  }

  const fmt = (n, digits = 1) => n.toLocaleString('de-DE', { maximumFractionDigits: digits });

  // German input: "72,5" or "72.5"; returns null for empty, NaN for garbage
  function parse(raw) {
    const s = String(raw).replace(/\s+/g, '').replace(',', '.');
    if (s === '') return null;
    if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
    return Number(s);
  }

  function init(root) {
    if (!root || root.dataset.kcalReady) return;
    root.dataset.kcalReady = 'true';

    const form = root.querySelector('[data-kcal-form]');
    const result = root.querySelector('[data-kcal-result]');
    const dialog = root.querySelector('[data-kcal-dialog]');
    const inputs = {};
    root.querySelectorAll('[data-kcal-input]').forEach((el) => (inputs[el.dataset.kcalInput] = el));
    const activity = root.querySelector('[data-kcal-activity]');
    const out = (name) => root.querySelector(`[data-kcal-out="${name}"]`);
    const missingEl = root.querySelector('[data-kcal-missing]');
    const warningEl = root.querySelector('[data-kcal-warning]');
    const warningText = root.querySelector('[data-kcal-warning-text]');
    const statusEl = root.querySelector('[data-kcal-status]');
    const rateGroups = [...root.querySelectorAll('[data-kcal-rate]')];

    const state = { gender: 'male', goal: 'maintain', rate: 0 };
    let statusTimer;

    const setPressed = (seg, value) => {
      seg.querySelectorAll('[data-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === value)));
    };

    function fieldState(key) {
      const v = parse(inputs[key].value);
      if (v === null) return { value: null, status: 'empty' };
      const [min, max] = LIMITS[key];
      if (Number.isNaN(v) || v < min || v > max) return { value: v, status: 'invalid' };
      return { value: v, status: 'ok' };
    }

    function showError(key, show) {
      const input = inputs[key];
      const err = root.querySelector(`#${CSS.escape(input.id)}-error`);
      const st = fieldState(key);
      let msg = '';
      if (show && st.status === 'empty') msg = EMPTY_MSG[key];
      if (show && st.status === 'invalid') {
        const [min, max] = LIMITS[key];
        msg = `Bitte ${fmt(min)}–${fmt(max)} ${UNITS[key]} eingeben.`;
      }
      input.setAttribute('aria-invalid', msg ? 'true' : 'false');
      if (err) {
        err.textContent = msg;
        err.hidden = !msg;
      }
      return !msg;
    }

    function updateRateGroups() {
      rateGroups.forEach((group) => {
        const active = group.dataset.kcalRate === state.goal;
        group.hidden = !active;
        if (!active) return;
        setPressed(group.querySelector('[data-kcal-seg]'), String(state.rate));
        const hint = group.querySelector('[data-kcal-rate-hint]');
        if (hint) {
          hint.textContent =
            state.goal === 'lose'
              ? `Entspricht einem Defizit von ${fmt(state.rate)} kcal pro Tag.`
              : `Entspricht einem Überschuss von ${fmt(state.rate)} kcal pro Tag.`;
        }
      });
    }

    function render() {
      const fields = {};
      const missing = [];
      const invalid = [];
      Object.keys(inputs).forEach((key) => {
        const st = fieldState(key);
        fields[key] = st.value;
        if (st.status === 'empty') missing.push(NAMES[key]);
        if (st.status === 'invalid') invalid.push(NAMES[key]);
      });

      if (missing.length || invalid.length) {
        result.setAttribute('data-empty', '');
        const parts = [];
        if (missing.length) parts.push(`Noch offen: ${missing.join(', ')}`);
        if (invalid.length) parts.push(`Bitte prüfen: ${invalid.join(', ')}`);
        missingEl.textContent = parts.join(' · ');
        clearTimeout(statusTimer);
        return null;
      }

      const r = calculate({
        gender: state.gender,
        age: fields.age,
        weight: fields.weight,
        height: fields.height,
        kfa: fields.kfa,
        activity: parseFloat(activity.value),
        goal: state.goal,
        rate: state.rate,
      });

      out('kcal').textContent = fmt(r.calories, 0);
      out('note').textContent =
        state.goal === 'lose'
          ? 'Empfohlene Kalorienzufuhr pro Tag zum Abnehmen.'
          : state.goal === 'gain'
            ? 'Empfohlene Kalorienzufuhr pro Tag für Zunahme und Muskelaufbau.'
            : 'Empfohlene Kalorienzufuhr pro Tag, um dein Gewicht zu halten.';
      out('protein').textContent = `${fmt(r.protein)} g`;
      out('carbs').textContent = `${fmt(r.carbs)} g`;
      out('fat').textContent = `${fmt(r.fat)} g`;
      out('bmr').textContent = `${fmt(r.bmr, 0)} kcal`;
      out('tdee').textContent = `${fmt(r.tdee, 0)} kcal`;

      // macro split by energy (protein/carbs 4 kcal per g, fat 9 kcal per g)
      const kcal = { protein: r.protein * 4, carbs: r.carbs * 4, fat: r.fat * 9 };
      const total = kcal.protein + kcal.carbs + kcal.fat || 1;
      Object.keys(kcal).forEach((key) => {
        const share = (kcal[key] / total) * 100;
        root.querySelector(`[data-kcal-bar="${key}"]`).style.width = `calc(${share}% - 2px)`;
        out(`${key}-share`).textContent = `${Math.round(share)} %`;
      });

      // warnings (same conditions and wording as before, German number format)
      warningText.textContent = '';
      if (r.aggressive) {
        const strong = document.createElement('strong');
        strong.textContent = 'Defizit zu aggressiv!';
        warningText.append(strong, ' Empfehlung: Max. 0,5–0,7 kg/Woche für optimalen Muskelerhalt.');
        if (r.carbsZeroed) warningText.append(' Kohlenhydrate mussten auf 0 g gesetzt werden – nicht empfehlenswert!');
      }
      if (r.recommended !== null) {
        warningText.append(
          `${r.aggressive ? ' ' : ''}Empfohlenes Defizit für Muskelerhalt: ${fmt(r.recommended, 0)} kcal/Tag (ca. 0,7 kg/Woche).`
        );
      }
      warningEl.hidden = !warningText.textContent;

      result.removeAttribute('data-empty');

      // screen readers: one calm announcement after typing pauses
      clearTimeout(statusTimer);
      statusTimer = setTimeout(() => {
        statusEl.textContent =
          `Ergebnis: ${fmt(r.calories, 0)} Kilokalorien pro Tag. Protein ${fmt(r.protein)} Gramm, ` +
          `Kohlenhydrate ${fmt(r.carbs)} Gramm, Fett ${fmt(r.fat)} Gramm.` +
          (warningText.textContent ? ` Hinweis: ${warningText.textContent}` : '');
      }, 900);
      return r;
    }

    // ---- segmented controls ----
    root.querySelectorAll('[data-kcal-seg]').forEach((seg) => {
      seg.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-value]');
        if (!btn) return;
        const kind = seg.dataset.kcalSeg;
        const value = btn.dataset.value;
        if (kind === 'gender') {
          state.gender = value;
          setPressed(seg, value);
          root.querySelectorAll('[data-kcal-kfa-set]').forEach((set) => (set.hidden = set.dataset.kcalKfaSet !== value));
        } else if (kind === 'goal') {
          state.goal = value;
          state.rate = RATE_DEFAULT[value];
          setPressed(seg, value);
          updateRateGroups();
        } else if (kind === 'rate') {
          state.rate = Number(value);
          updateRateGroups();
        }
        render();
      });
    });

    // ---- inputs ----
    Object.entries(inputs).forEach(([key, input]) => {
      input.addEventListener('input', () => {
        // clear a shown error as soon as the value becomes valid
        if (input.getAttribute('aria-invalid') === 'true' && fieldState(key).status === 'ok') showError(key, false);
        render();
      });
      input.addEventListener('blur', () => {
        showError(key, fieldState(key).status === 'invalid');
      });
    });
    activity.addEventListener('change', render);

    // ---- "Jetzt berechnen" (phones/tablets): show all errors or jump to the result ----
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      let firstBad = null;
      Object.keys(inputs).forEach((key) => {
        if (!showError(key, true) && !firstBad) firstBad = inputs[key];
      });
      if (firstBad) {
        firstBad.focus();
        return;
      }
      render();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      result.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });

    // ---- KFA examples ----
    const openBtn = root.querySelector('[data-kcal-kfa-open]');
    const closeBtn = root.querySelector('[data-kcal-kfa-close]');
    let scrollLock = '';

    function openDialog() {
      if (!dialog || dialog.open) return;
      const current = parse(inputs.kfa.value);
      dialog.querySelectorAll('[data-kfa]').forEach((b) => {
        b.setAttribute('aria-current', String(current !== null && Number(b.dataset.kfa) === current));
      });
      scrollLock = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
    }

    function closeDialog() {
      if (dialog && dialog.open) dialog.close();
    }

    if (dialog) {
      openBtn && openBtn.addEventListener('click', openDialog);
      closeBtn && closeBtn.addEventListener('click', closeDialog);
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = scrollLock;
        openBtn && openBtn.focus();
      });
      // click on the backdrop closes
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });
      dialog.addEventListener('click', (event) => {
        const item = event.target.closest('[data-kfa]');
        if (!item) return;
        inputs.kfa.value = fmt(Number(item.dataset.kfa));
        showError('kfa', false);
        closeDialog();
        render();
      });
    }

    updateRateGroups();
    render();
  }

  const boot = (scope) => (scope || document).querySelectorAll('[data-evo-kcal]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
